// Campus admin portal helpers. Reads + writes for the new
// scoped_university_id pattern (Prompt 11) plus the legacy
// campus_admin_university() helpers for the existing students /
// competitions roster RPCs.
//
// Read helpers return null / [] on miss; write helpers throw with the
// RPC's error message so the calling UI can surface it.

import type {
  CampusCompetitionRow,
  CampusCompetitionStudentRow,
  CampusStudentRow,
} from "../types/database";
import { supabase } from "./supabase";

export type UniversityProfile = {
  id: string;
  name: string;
  primary_email_domain: string | null;
  alt_email_domains: string[];
  is_active: boolean;
  logo_url: string | null;
  website_url: string | null;
  short_description: string | null;
  career_services_email: string | null;
  career_services_phone: string | null;
  primary_color_hex: string | null;
  profile_updated_at: string | null;
};

export type CampusScope = {
  university_id: string;
  university: UniversityProfile | null;
};

export type CampusStudentDetail = CampusStudentRow & {
  /** From students.school_verified — drives the vouch CTA. */
  school_verified: boolean;
  school_verification_method: "email_domain" | "manual_admin" | "self_attested" | null;
  /** True when this user's email has been confirmed by the campus
   *  admin via a previous vouch row (resolved or not). */
  vouched_by_me: boolean;
  flagged_by_me: boolean;
};

export type SchoolVouchRow = {
  id: string;
  student_id: string;
  university_id: string;
  vouched_by: string;
  signal: "vouch" | "flag";
  reason: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
};

// ─── Reads ────────────────────────────────────────────────────────────

/** Resolve the calling campus_admin's scoped university UUID. Returns
 *  null when the caller isn't a campus_admin. */
export async function getCampusScope(): Promise<string | null> {
  const { data, error } = await supabase.rpc("current_user_campus_scope");
  if (error) return null;
  return typeof data === "string" && data.length ? data : null;
}

/** Load the full university profile row for the caller's scope. Used by
 *  the dashboard banner + the profile editor. */
export async function getMyUniversityProfile(): Promise<UniversityProfile | null> {
  const scope = await getCampusScope();
  if (!scope) {
    // Legacy fallback: text-based campus_admin via campus_admins.university_name.
    // Resolve it to a university by name match so older accounts still load.
    const { data: legacy } = await supabase.rpc("campus_admin_university");
    if (typeof legacy === "string" && legacy.length > 0) {
      const { data: uni } = await supabase
        .from("universities")
        .select(
          "id, name, primary_email_domain, alt_email_domains, is_active, logo_url, website_url, short_description, career_services_email, career_services_phone, primary_color_hex, profile_updated_at",
        )
        .ilike("name", legacy)
        .maybeSingle();
      return (uni as UniversityProfile | null) ?? null;
    }
    return null;
  }
  const { data, error } = await supabase
    .from("universities")
    .select(
      "id, name, primary_email_domain, alt_email_domains, is_active, logo_url, website_url, short_description, career_services_email, career_services_phone, primary_color_hex, profile_updated_at",
    )
    .eq("id", scope)
    .maybeSingle();
  if (error || !data) return null;
  return data as UniversityProfile;
}

/** Existing legacy helper used by RLS-gated RPCs. Kept for backward compat. */
export async function getMyUniversity(): Promise<string | null> {
  const profile = await getMyUniversityProfile();
  return profile?.name ?? null;
}

export async function listMyStudents(): Promise<CampusStudentRow[]> {
  const { data, error } = await supabase.rpc("campus_admin_my_students");
  if (error) throw new Error(error.message);
  return (data ?? []) as CampusStudentRow[];
}

/** Hydrate the roster with verification + vouch flags so the students
 *  page can render the vouch / flag CTAs. */
export async function listMyStudentsWithVerification(): Promise<CampusStudentDetail[]> {
  const base = await listMyStudents();
  if (base.length === 0) return [];

  const ids = base.map((r) => r.student_id);
  const { data: { user } } = await supabase.auth.getUser();
  const myUid = user?.id ?? null;

  const [{ data: students }, { data: vouches }] = await Promise.all([
    supabase
      .from("students")
      .select("id, school_verified, school_verification_method")
      .in("id", ids),
    supabase
      .from("student_school_vouches")
      .select("student_id, signal, vouched_by")
      .in("student_id", ids),
  ]);

  const studById = new Map(
    (students ?? []).map((s) => [s.id as string, s as { school_verified: boolean; school_verification_method: CampusStudentDetail["school_verification_method"] }]),
  );
  const myVouches = new Set<string>();
  const myFlags = new Set<string>();
  for (const v of (vouches ?? []) as SchoolVouchRow[]) {
    if (myUid && v.vouched_by === myUid) {
      if (v.signal === "vouch") myVouches.add(v.student_id);
      if (v.signal === "flag") myFlags.add(v.student_id);
    }
  }

  return base.map((row) => {
    const s = studById.get(row.student_id);
    return {
      ...row,
      school_verified: s?.school_verified ?? false,
      school_verification_method: s?.school_verification_method ?? null,
      vouched_by_me: myVouches.has(row.student_id),
      flagged_by_me: myFlags.has(row.student_id),
    };
  });
}

export async function listMyCompetitions(): Promise<CampusCompetitionRow[]> {
  const { data, error } = await supabase.rpc("campus_admin_my_competitions");
  if (error) throw new Error(error.message);
  return (data ?? []) as CampusCompetitionRow[];
}

export async function listCompetitionStudents(
  competitionId: string,
): Promise<CampusCompetitionStudentRow[]> {
  const { data, error } = await supabase.rpc("campus_admin_competition_students", {
    p_competition_id: competitionId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as CampusCompetitionStudentRow[];
}

/** "Featured open cases" for the dashboard. Lists ≤5 cases the caller's
 *  students could join: status = open, eligibility includes their
 *  school (or is unset). */
export async function listFeaturedOpenCases(): Promise<
  Array<{
    id: string;
    name: string;
    company_name: string | null;
    engagement_model: string;
    scholarship_cents: number;
    closes_at: string | null;
  }>
> {
  const profile = await getMyUniversityProfile();
  if (!profile) return [];

  const { data } = await supabase
    .from("competitions")
    .select(
      "id, name, company_id, engagement_model, eligibility_schools, prize_structure, closes_at, companies:company_id(name)",
    )
    .eq("status", "open")
    .order("closes_at", { ascending: true });
  if (!data) return [];

  const myName = profile.name.toLowerCase();
  return data
    .filter((c) => {
      const allowed = (c.eligibility_schools as string[] | null) ?? [];
      if (!allowed || allowed.length === 0) return true;
      return allowed.some((s) => typeof s === "string" && s.toLowerCase() === myName);
    })
    .slice(0, 5)
    .map((c) => {
      const company = Array.isArray(c.companies) ? c.companies[0] : c.companies;
      const scholarshipCents = Array.isArray(c.prize_structure)
        ? (c.prize_structure as { amount_cents?: number }[]).reduce(
            (sum, p) => sum + (typeof p?.amount_cents === "number" ? p.amount_cents : 0),
            0,
          )
        : 0;
      return {
        id: c.id as string,
        name: c.name as string,
        company_name: (company?.name as string | undefined) ?? null,
        engagement_model: c.engagement_model as string,
        scholarship_cents: scholarshipCents,
        closes_at: (c.closes_at as string | null) ?? null,
      };
    });
}

// ─── Writes ───────────────────────────────────────────────────────────

export async function vouchStudent(studentId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc("campus_admin_vouch_student", {
    p_student_id: studentId,
    p_signal: "vouch",
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function flagStudent(studentId: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new Error("Reason is required when flagging a student");
  const { error } = await supabase.rpc("campus_admin_vouch_student", {
    p_student_id: studentId,
    p_signal: "flag",
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export type UniversityProfilePatch = Partial<
  Pick<
    UniversityProfile,
    "logo_url" | "website_url" | "short_description" | "career_services_email" | "career_services_phone" | "primary_color_hex"
  >
>;

export async function updateMyUniversityProfile(patch: UniversityProfilePatch): Promise<void> {
  const { error } = await supabase.rpc("update_my_university_profile", {
    p_logo_url: patch.logo_url ?? null,
    p_website_url: patch.website_url ?? null,
    p_short_description: patch.short_description ?? null,
    p_career_services_email: patch.career_services_email ?? null,
    p_career_services_phone: patch.career_services_phone ?? null,
    p_primary_color_hex: patch.primary_color_hex ?? null,
  });
  if (error) throw new Error(error.message);
}
